import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useState, useRef, useCallback, useEffect } from 'react';
import Input from '@/components/forms/Input';
import Textarea from '@/components/forms/Textarea';
import Button from '@/components/ui/Button';
import { getClientProfile, updateClientProfile } from '@/services/clientProfile';
import showToast from '@/components/ui/Toast';
import Select from '@/components/forms/Select';
import ProfileImageUpload from '@/components/forms/ProfileImageUpload';

// Types
interface AccountValues {
  fullName: string;
  bio: string;
  username: string;
  dob: string;
  email: string;
  gender: string;
  phone: string;
  domicile: string;
  profileImage: string;
}

const accountInitial: AccountValues = {
  fullName: '',
  bio: '',
  username: '',
  dob: '',
  email: '',
  gender: '',
  phone: '',
  domicile: '',
  profileImage: '',
};

const accountSchema = Yup.object({
  fullName: Yup.string().required('Nama lengkap wajib diisi'),
  email: Yup.string().email('Format email tidak valid'),
});

const AccountInformationTab = () => {
  const [initialValues, _setInitialValues] = useState<AccountValues>(accountInitial);
  const mountedRef = useRef(true);
  const genderOptions = [
    { value: 'male', label: 'Laki-laki' },
    { value: 'female', label: 'Perempuan' },
  ];

  // Placeholder for loading existing user data if needed later
  const fetchProfile = useCallback(async () => {
    const resp = await getClientProfile();
    if (resp.success) {
      _setInitialValues({
        fullName: resp.data.name || '',
        bio: resp.data.bio || '',
        username: resp.data.username || '',
        dob: resp.data.birthdate || '',
        email: resp.data.email || '',
        gender: resp.data.gender || '',
        phone: resp.data.phone || '',
        domicile: resp.data.domicile || '',
        profileImage: resp.data.profile_image || '',
      });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchProfile();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchProfile]);

  return (
    <div className="p-6">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-4">
          <h3 className="text-xl font-medium text-gray-800">Profil Pengguna</h3>
        </div>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={accountSchema}
          onSubmit={async (values: any, helpers) => {
            try {
              // Build payload compatible with updateClientProfile
              const payload: any = {
                name: values.fullName || undefined,
                username: values.username || undefined,
                phone: values.phone || undefined,
                bio: values.bio || undefined,
                birth_date: values.dob || undefined,
                birth_place: undefined,
                gender: values.gender || undefined,
                domicile: values.domicile || undefined,
              };

              // Include file if user selected one (we store File in profileImageFile)
              if (values.profileImageFile) payload.profile_image = values.profileImageFile;

              const resp = await updateClientProfile(payload);
              if (resp && resp.success) {
                showToast.success('Profil berhasil diperbarui');
                // refresh profile values if endpoint returned updated data
                if (resp.data) {
                  helpers.resetForm({
                    values: {
                      fullName: resp.data.name || '',
                      bio: resp.data.bio || '',
                      username: resp.data.username || '',
                      dob: resp.data.birthdate || '',
                      email: resp.data.email || '',
                      gender: resp.data.gender || '',
                      phone: resp.data.phone || '',
                      domicile: resp.data.domicile || '',
                      profileImage: resp.data.profile_image || '',
                    },
                  });
                  localStorage.setItem('clientName', resp.data.name || '');
                  localStorage.setItem('clientAvatar', resp.data.profile_image || '');
                  // Dispatch custom event to notify Topbar
                  window.dispatchEvent(new Event('localStorageUserUpdate'));
                }
              } else {
                showToast.error(resp?.message || 'Gagal memperbarui profil');
              }
            } catch (err: any) {
              const message = err?.response?.data?.message || err?.message || 'Terjadi kesalahan';
              showToast.error(message);
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-5 items-start">
                <div className="col-span-12 md:col-span-1 flex flex-col items-center md:items-center">
                  <ProfileImageUpload name="profileImage" className="w-36 h-36" />
                </div>

                <div className="col-span-12 md:col-span-2">
                  <div className="flex flex-col gap-4">
                    <Input
                      name="fullName"
                      label="Nama Lengkap"
                      placeholder="Isikan Nama Lengkap Anda"
                    />
                    <Input name="username" label="Username" placeholder="Username Anda" />
                    <Input name="email" disabled label="Email" placeholder="Email Anda" />
                    <Textarea name="bio" label="Bio Singkat" placeholder="Bio Anda" />
                  </div>
                </div>

                <div className="col-span-12 md:col-span-2">
                  <div className="flex flex-col gap-4">
                    <Input
                      name="phone"
                      label="Nomor telepon"
                      placeholder="Nomor telepon / Whatsapp"
                    />
                    <Input
                      name="dob"
                      label="Tanggal Lahir"
                      type="date"
                      placeholder="Tanggal Lahir Anda"
                    />
                    <Select
                      name="gender"
                      label="Gender"
                      options={genderOptions}
                      placeholder="Gender"
                      required
                    />
                    <Input name="domicile" label="Domisili" placeholder="Domisili Anda" />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-start">
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-6"
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AccountInformationTab;
